/**
 * D283: the 3D world is the default for every player. "Flat map" is the settings switch that
 * opts back out, off by default, per device, in localStorage. A new key, so that an old "off"
 * saved during the D274 trial does not keep anyone on the flat map. Storage that throws (a
 * private window) keeps the default. This file is in the main bundle, so it imports nothing
 * from three.
 */
import { useEffect, useState } from 'preact/hooks';

export const FLAT_MAP_KEY = 'hedgerow-flat-map';

function read(): boolean {
  try {
    return globalThis.localStorage?.getItem(FLAT_MAP_KEY) === '1';
  } catch {
    return false;
  }
}

let flatMapOn = read();
const listeners = new Set<(on: boolean) => void>();

export function isFlatMap(): boolean {
  return flatMapOn;
}

export function setFlatMap(value: boolean): void {
  flatMapOn = value;
  try {
    globalThis.localStorage?.setItem(FLAT_MAP_KEY, value ? '1' : '0');
  } catch {
    // Kept for this session only.
  }
  for (const fn of listeners) fn(value);
}

/** The settings row and the world screen: `[on, set]`. */
export function useFlatMap(): [boolean, (on: boolean) => void] {
  const [value, setValue] = useState(flatMapOn);
  useEffect(() => {
    listeners.add(setValue);
    return () => void listeners.delete(setValue);
  }, []);
  return [value, setFlatMap];
}

/**
 * D305: "Show level signs" hides the tier sign (D268) in both renderers. On by default, kept
 * per device the same way as the flat-map switch: a missing key means on.
 */
export const SHOW_SIGNS_KEY = 'hedgerow-show-signs';

function readShowSigns(): boolean {
  try {
    const v = globalThis.localStorage?.getItem(SHOW_SIGNS_KEY);
    return v !== '0';
  } catch {
    return true;
  }
}

let showSignsOn = readShowSigns();
const signListeners = new Set<(on: boolean) => void>();

export function isShowSigns(): boolean {
  return showSignsOn;
}

export function setShowSigns(value: boolean): void {
  showSignsOn = value;
  try {
    globalThis.localStorage?.setItem(SHOW_SIGNS_KEY, value ? '1' : '0');
  } catch {
    // Kept for this session only.
  }
  for (const fn of signListeners) fn(value);
}

/** The settings row and the world screen: `[on, set]`. */
export function useShowSigns(): [boolean, (on: boolean) => void] {
  const [value, setValue] = useState(showSignsOn);
  useEffect(() => {
    signListeners.add(setValue);
    return () => void signListeners.delete(setValue);
  }, []);
  return [value, setShowSigns];
}
